import React, { useMemo, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { useClients } from "../contexts/ClientsContext";
import ProjectLightbox from "../components/ProjectLightbox";
import ShareCaseStudy from "../components/ShareCaseStudy";

const ClientDetailPage = () => {
  const { slug } = useParams();
  const { clients, loading } = useClients();
  const client = clients.find((c) => c.slug === slug);
  const [open, setOpen] = useState(false);
  const [idx, setIdx] = useState(0);
  const [filter, setFilter] = useState("All");

  const types = useMemo(() => {
    if (!client) return [];
    return ["All", ...Array.from(new Set(client.projects.map((p) => p.type)))];
  }, [client]);

  const visibleProjects = useMemo(() => {
    if (!client) return [];
    return filter === "All" ? client.projects : client.projects.filter((p) => p.type === filter);
  }, [client, filter]);

  if (!client) {
    if (loading) return <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-32 text-stone-300 font-mono-label">Loading…</div>;
    return <Navigate to="/clients" replace />;
  }

  const currentIndex = clients.findIndex((c) => c.slug === slug);
  const next = clients[(currentIndex + 1) % clients.length];
  const prev = clients[(currentIndex - 1 + clients.length) % clients.length];

  const openAt = (i) => { setIdx(i); setOpen(true); };

  return (
    <div>
      {/* HEADER */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 pt-12 md:pt-20 pb-12">
        <Link to="/clients" className="inline-flex items-center gap-2 font-mono-label text-stone-300 hover:text-stone-100 mb-10">
          <ArrowLeft size={14} /> All Clients
        </Link>

        <div className="grid md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-8 fade-up">
            <p className="font-mono-label text-[#F62493] mb-5">
              Client {String(currentIndex + 1).padStart(2, "0")} / {String(clients.length).padStart(2, "0")} · {client.industry}
            </p>
            <h1 className="font-display text-5xl md:text-7xl lg:text-8xl text-stone-100 leading-[0.95] tracking-[-0.02em]">
              {client.name}
            </h1>
          </div>
          <div className="md:col-span-4 fade-up delay-1">
            <p className="text-lg text-stone-200 leading-relaxed">{client.summary}</p>
            <div className="mt-6 flex gap-8">
              <div>
                <div className="font-mono-label text-stone-300">Works</div>
                <div className="font-display text-3xl text-stone-100">{client.projects.length}</div>
              </div>
              <div>
                <div className="font-mono-label text-stone-300">Category</div>
                <div className="font-display text-xl text-stone-100 leading-snug">{client.industry}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CASE STUDY (Before / After) — only if either field is set */}
      {(client.before || client.after || (client.metrics && client.metrics.length > 0)) && (
        <section className="max-w-[1400px] mx-auto px-6 md:px-10 pb-6 fade-up">
          <div className="pt-8 border-t border-stone-800">
            <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
              <p className="font-mono-label text-[#F62493]">The Story · Before → After</p>
              <ShareCaseStudy client={client} />
            </div>
            <div className="grid md:grid-cols-2 gap-6 md:gap-10">
              {client.before && (
                <div className="relative bg-[#141414] border border-stone-800 rounded-sm p-7 md:p-9">
                  <span className="absolute top-4 right-4 font-mono-label text-stone-300 text-[10px]">01</span>
                  <p className="font-mono-label text-stone-300 mb-3">Before — The Challenge</p>
                  <p className="font-display text-2xl md:text-3xl text-stone-100 leading-snug whitespace-pre-line">
                    {client.before}
                  </p>
                </div>
              )}
              {client.after && (
                <div className="relative bg-[#1F1A0F] border-2 border-[#F62493] rounded-sm p-7 md:p-9">
                  <span className="absolute top-4 right-4 font-mono-label text-[#F62493] text-[10px]">02</span>
                  <p className="font-mono-label text-[#F62493] mb-3">After — The Impact</p>
                  <p className="font-display text-2xl md:text-3xl text-stone-100 leading-snug whitespace-pre-line">
                    {client.after}
                  </p>
                </div>
              )}
            </div>
            {client.metrics && client.metrics.length > 0 && (
              <div className="mt-6 md:mt-8 flex flex-wrap gap-2.5" data-testid="case-study-metrics">
                {client.metrics.map((m, i) => (
                  <span
                    key={i}
                    className="inline-flex items-center rounded-full bg-[#F62493] text-white px-5 py-2 font-mono-label text-xs md:text-sm"
                  >
                    {m}
                  </span>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* FILTERS */}
      {types.length > 2 && (
        <section className="max-w-[1400px] mx-auto px-6 md:px-10 pb-6">
          <div className="flex flex-wrap items-center gap-2 pt-6 border-t border-stone-800">
            {types.map((t) => (
              <button
                key={t}
                onClick={() => setFilter(t)}
                className={`font-mono-label px-4 py-2 rounded-full border transition-colors ${
                  filter === t
                    ? "bg-[#171717] text-[#F7F3ED] border-stone-900"
                    : "border-stone-400 text-stone-200 hover:border-stone-900"
                }`}
              >
                {t}
              </button>
            ))}
          </div>
        </section>
      )}

      {/* GALLERY */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-12">
        <div className="masonry">
          {visibleProjects.map((p, i) => (
            <button
              key={p.id}
              onClick={() => openAt(client.projects.findIndex((x) => x.id === p.id))}
              className="group block w-full text-left"
            >
              <div
                className="relative overflow-hidden bg-[#242424] mx-auto"
                style={p.scale ? { maxWidth: `${Math.round(p.scale * 100)}%` } : undefined}
              >
                <img
                  src={p.image}
                  alt={p.title}
                  loading="lazy"
                  className="w-full h-auto object-cover img-fade transition-transform duration-700 group-hover:scale-[1.03]"
                />
                <div className="absolute inset-0 bg-[#171717]/0 group-hover:bg-[#171717]/20 transition-colors duration-300" />
              </div>
              <div className="flex items-start justify-between pt-3 pb-1">
                <div>
                  <p className="font-mono-label text-stone-300">{p.type}</p>
                  <p className="text-stone-200 mt-1">{p.title}</p>
                </div>
                <span className="font-mono-label text-stone-300 pt-1">{String(i + 1).padStart(2, "0")}</span>
              </div>
            </button>
          ))}
        </div>
      </section>

      <ProjectLightbox
        open={open}
        onOpenChange={setOpen}
        projects={client.projects}
        index={idx}
        setIndex={setIdx}
        clientName={client.name}
      />

      {/* PREV / NEXT */}
      <section className="bg-[#171717] text-[#F7F3ED] mt-16">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-16 grid md:grid-cols-2 gap-10">
          <Link to={`/clients/${prev.slug}`} className="group block">
            <p className="font-mono-label text-stone-300">← Previous Client</p>
            <h3 className="font-display text-3xl md:text-5xl mt-3 group-hover:text-[#E8B0B7] transition-colors">{prev.name}</h3>
            <p className="font-mono-label text-stone-300 mt-2">{prev.industry}</p>
          </Link>
          <Link to={`/clients/${next.slug}`} className="group block md:text-right">
            <p className="font-mono-label text-stone-300">Next Client →</p>
            <h3 className="font-display text-3xl md:text-5xl mt-3 group-hover:text-[#E8B0B7] transition-colors">{next.name}</h3>
            <p className="font-mono-label text-stone-300 mt-2">{next.industry}</p>
            <span className="inline-flex items-center gap-2 mt-4 font-mono-label text-stone-200">
              View work <ArrowUpRight size={14} />
            </span>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ClientDetailPage;
